import React, { useState } from 'react';
import { ChevronRightIcon, ChevronLeftIcon, SettingsIcon, BuildingIcon, DatabaseIcon, CalculatorIcon, LayoutIcon, FileTextIcon, ShieldIcon, NetworkIcon, ServerIcon, BoxIcon, UploadIcon, ImageIcon, PlusIcon, ChevronDownIcon } from 'lucide-react';
import { FoundationalSettings } from '../components/settings/FoundationalSettings';
import { DataDefinitionsSettings } from '../components/settings/DataDefinitionsSettings';
import { CalculationRulesSettings } from '../components/settings/CalculationRulesSettings';
import { WorkflowSettings } from '../components/settings/WorkflowSettings';
import { UIUXSettings } from '../components/settings/UIUXSettings';
import { DocumentTemplatesSettings } from '../components/settings/DocumentTemplatesSettings';
import { SecuritySettings } from '../components/settings/SecuritySettings';
import { IntegrationSettings } from '../components/settings/IntegrationSettings';
import { DataManagementSettings } from '../components/settings/DataManagementSettings';
type TabId = 'foundational' | 'data-definitions' | 'calculation-rules' | 'workflow' | 'ui-ux' | 'templates' | 'security' | 'integration' | 'data-management';
interface TabItem {
  id: TabId;
  name: string;
  icon: React.ElementType;
  description: string;
}
const tabs: TabItem[] = [{
  id: 'foundational',
  name: 'Foundational & Organizational',
  icon: BuildingIcon,
  description: 'Basic system identity and organizational settings'
}, {
  id: 'data-definitions',
  name: 'Data Definitions & Classifications',
  icon: DatabaseIcon,
  description: 'Manage system-wide categories and classifications'
}, {
  id: 'calculation-rules',
  name: 'Calculation Rules & Logic',
  icon: CalculatorIcon,
  description: 'Configure financial calculations and business rules'
}, {
  id: 'workflow',
  name: 'Workflow & Process Automation',
  icon: BoxIcon,
  description: 'Define automated processes and approval flows'
}, {
  id: 'ui-ux',
  name: 'User Interface & Experience',
  icon: LayoutIcon,
  description: 'Customize system appearance and behavior'
}, {
  id: 'templates',
  name: 'Presentation & Documents',
  icon: FileTextIcon,
  description: 'Manage document templates and layouts'
}, {
  id: 'security',
  name: 'Security & Access Control',
  icon: ShieldIcon,
  description: 'Configure security policies and access controls'
}, {
  id: 'integration',
  name: 'Integration & Interoperability',
  icon: NetworkIcon,
  description: 'Manage external system connections'
}, {
  id: 'data-management',
  name: 'Data Management & Operations',
  icon: ServerIcon,
  description: 'Control data handling and operational rules'
}];
export const SettingsPage = () => {
  const [currentTab, setCurrentTab] = useState<TabId>('foundational');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const handleSave = (tabId: TabId) => {
    // Implement save logic for the current tab
    console.log(`Saving settings for ${tabId}`);
  };
  const renderTabContent = () => {
    switch (currentTab) {
      case 'foundational':
        return <FoundationalSettings data-id="element-5241" />;
      case 'data-definitions':
        return <DataDefinitionsSettings data-id="element-5242" />;
      case 'calculation-rules':
        return <CalculationRulesSettings data-id="element-5243" />;
      case 'workflow':
        return <WorkflowSettings data-id="element-5244" />;
      case 'ui-ux':
        return <UIUXSettings data-id="element-5245" />;
      case 'templates':
        return <DocumentTemplatesSettings data-id="element-5246" />;
      case 'security':
        return <SecuritySettings data-id="element-5247" />;
      case 'integration':
        return <IntegrationSettings data-id="element-5248" />;
      case 'data-management':
        return <DataManagementSettings data-id="element-5249" />;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-5250">
      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5251">
        {/* Breadcrumb with improved hover states */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-5252">
          <a href="/" className="hover:text-gray-900 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded" data-id="element-5253">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2 flex-shrink-0" data-id="element-5254" />
          <span className="text-gray-900 font-medium" data-id="element-5255">System Settings</span>
        </div>
        {/* Header with enhanced visual hierarchy */}
        <div className="mb-12" data-id="element-5256">
          <div className="flex items-center space-x-4" data-id="element-5257">
            <div className="p-2 bg-gray-100 rounded-lg" data-id="element-5258">
              <SettingsIcon className="h-8 w-8 text-gray-900" data-id="element-5259" />
            </div>
            <div data-id="element-5260">
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight" data-id="element-5261">
                System Settings
              </h1>
              <p className="text-gray-600 mt-1 text-lg" data-id="element-5262">
                Configure and manage all system-wide settings and preferences
              </p>
            </div>
          </div>
        </div>
        {/* Main Content with improved layout */}
        <div className="flex gap-8" data-id="element-5263">
          {/* Vertical Tabs Navigation with enhanced animations */}
          <div className={`
              ${sidebarCollapsed ? 'w-16' : 'w-72'}
              flex-shrink-0 transition-all duration-300 ease-in-out
              border-r border-gray-200
            `} data-id="element-5264">
            <div className="flex items-center justify-between mb-6" data-id="element-5265">
              <h2 className={`
                  font-medium text-gray-900 text-lg
                  ${sidebarCollapsed ? 'opacity-0 w-0' : 'opacity-100'}
                  transition-all duration-300
                `} data-id="element-5266">
                Settings Menu
              </h2>
              <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'} data-id="element-5267">
                {sidebarCollapsed ? <ChevronRightIcon className="h-5 w-5 text-gray-500" data-id="element-5268" /> : <ChevronLeftIcon className="h-5 w-5 text-gray-500" data-id="element-5269" />}
              </button>
            </div>
            <nav className="space-y-1" data-id="element-5270">
              {tabs.map(tab => {
              const colors = {
                foundational: 'text-blue-600',
                'data-definitions': 'text-purple-600',
                'calculation-rules': 'text-green-600',
                workflow: 'text-orange-600',
                'ui-ux': 'text-indigo-600',
                templates: 'text-pink-600',
                security: 'text-red-600',
                integration: 'text-teal-600',
                'data-management': 'text-cyan-600'
              };
              const bgColors = {
                foundational: 'bg-blue-50',
                'data-definitions': 'bg-purple-50',
                'calculation-rules': 'bg-green-50',
                workflow: 'bg-orange-50',
                'ui-ux': 'bg-indigo-50',
                templates: 'bg-pink-50',
                security: 'bg-red-50',
                integration: 'bg-teal-50',
                'data-management': 'bg-cyan-50'
              };
              return <button key={tab.id} onClick={() => setCurrentTab(tab.id)} className={`
                      w-full flex items-center px-4 py-3 text-sm font-medium
                      rounded-lg transition-all duration-200
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                      ${currentTab === tab.id ? `${bgColors[tab.id]} ${colors[tab.id]} shadow-sm` : 'text-gray-600 hover:bg-gray-50'}
                      ${currentTab === tab.id ? 'border-l-4 border-current' : 'border-l-4 border-transparent'}
                    `} title={sidebarCollapsed ? tab.name : undefined} aria-current={currentTab === tab.id ? 'page' : undefined} data-id="element-5271">
                    <tab.icon className={`
                        h-5 w-5 flex-shrink-0 transition-colors duration-200
                        ${currentTab === tab.id ? colors[tab.id] : 'text-gray-400 group-hover:text-gray-500'}
                      `} aria-hidden="true" data-id="element-5272" />
                    {!sidebarCollapsed && <span className="ml-3 truncate transition-opacity duration-200" data-id="element-5273">
                        {tab.name}
                      </span>}
                  </button>;
            })}
            </nav>
          </div>
          {/* Content Area with improved styling */}
          <div className="flex-1 min-w-0" data-id="element-5274">
            <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden" data-id="element-5275">
              <div className="p-8" data-id="element-5276">
                {/* Tab Header with enhanced hierarchy */}
                <div className="mb-8 border-b border-gray-200 pb-6" data-id="element-5277">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-5278">
                    {tabs.find(tab => tab.id === currentTab)?.name}
                  </h2>
                  <p className="text-base text-gray-600" data-id="element-5279">
                    {tabs.find(tab => tab.id === currentTab)?.description}
                  </p>
                </div>
                {/* Form Content with consistent spacing */}
                <div className="space-y-8" data-id="element-5280">{renderTabContent()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};